import React, { useEffect, useState, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box,
  FormControlLabel,
  Grid,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Calculate, Help, PriorityHigh } from '@mui/icons-material';
import { string, object, boolean, number } from 'yup';
import { GPAConversionDialog } from './GPAConversionDialog';
import { GPAConversionDialogV2 } from './GPAConversionDialogV2';
import {
  fieldOfStudyOptions,
  levelOfDegreeOptions,
  mediumOfInstructionOptions,
  initialQualificationStatusOptions as initialQSFormat,
  initialCurrentStudyYearOptions,
  mediumOfQualificationOptions,
  awardClassificationOptions,
} from './options';
import type {
  AcademicQualificationForm,
  NewFiles,
  DialogV2ContentValue,
  DialogContentValue,
} from './types';
import { editAcademicQualifyPermission } from './editAcademicQualifyPermission';
import {
  QUALIFICATION_STATUS_TYPES,
  QUALIFICATION_MEANS,
  APPLICATION_MODULE,
  AWARD_CLASSIFICATION as AWARD_CLASSIFICATION_TYPE,
} from '../constants';
import { modeOfStudyOptions } from '../ProgrammeChoice';
import { PAGE_TEXT } from '../lang';
import { APPLICATION_STATUS_MODULE } from '../../ApplicationStatus/constants';
import { convertEntryYearMap } from '../../const';
import {
  type SupportingDocuments,
  type AcademicQualificationFormData,
  addApplicationItem,
  updateApplicationItem,
  DEGREE_LEVEL,
} from '../../../graphql';
import {
  Alert,
  Select,
  SearchableSelect,
  TextField,
  IconButton,
  Checkbox,
  Button,
  FilesPanel,
  SwitchButton,
  DatePicker,
  FILE_MANAGE_ORDER,
  type AlertDetails,
  type FileData,
  type SelectOption,
  Radio,
  Dialog,
} from '../../../../../components';
import {
  handleReadySubmit,
  handleFileRename,
  type FormController,
  FormModificationMode,
} from '../../../../../utils';

type AcademicQualifyBoxProps = {
  index: number;
  applicationNo: string;
  initialValues?: AcademicQualificationFormData;
  enableEdit: FormController;
  countryList: SelectOption[];
  universityList: SelectOption[];
  entryYear?: string;
  enableDelete: boolean;
  handleDelete: (index: number) => void;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

const {
  LEVEL_OF_DEGREE,
  NAME_OF_PROGRAMME_AWARD,
  PRESENT,
  FROM,
  TO,
  MODE_OF_STUDY,
  CURRENT_STUDY_YEAR,
  NAME_OF_INSTITUTION,
  COUNTRY_REGION_OF_INSTITUTION,
  MEDIUM_OF_INSTRUCTION,
  OTHER_MEDIUM,
  FIELD_OF_STUDY,
  MEDIUM_OF_QUALIFICATION,
  QUALIFICATION_STATUS,
  DATE_OF_AWARD,
  WITHDRAWAL_DATE,
  EXPECTED_DATE,
  AWARD_CLASSIFICATION,
  HAVE_ACADEMIC_CREDENTIAL,
  ACADEMIC_CREDENTIAL_INTRO,
  HAVE_DEGREE_CERTIFICATE,
  ONLINE_VERIFICATION,
  DEGREE_VERIFICATION,
  NO_OFFICIAL_MARK_GPA,
  OFFICIAL_MARK,
  AWARD_CUM_GPA_AVG_MARK,
  CONVERTED_SCORE,
  NO_OFFICIAL_RANK,
  RANK_IN_CLASS,
  RANK,
  OUT_OF,
  HAVE_CGPA,
  HELPER_TEXT,
  GPA_DIALOG,
  FILES_PANEL,
  DEGREE_CERTIFICATE_INTRO,
} = PAGE_TEXT.QUALIFICATION.ACADEMIC_QUALIFICATION;

const CHN = 'CHN';
export const AcademicQualifyBox: React.FunctionComponent<
  AcademicQualifyBoxProps
> = (props: AcademicQualifyBoxProps) => {
  const {
    index: academicQualifyIndex,
    applicationNo,
    enableDelete,
    initialValues = {
      presentIndicator: false,
      gpaIndicator: false,
      rankIndicator: false,
      academicCredentialIndicator: false,
      degreeCertIndicator: false,
      degreeLevel: '',
      programmeName: '',
      from: null,
      to: null,
      studyMode: '',
      currentStudyYear: undefined,
      institutionName: '',
      institutionCountry: '',
      instructionMedium: '',
      otherInstructionMedium: '',
      studyField: '',
      qualificationMeans: '',
      qualificationStatus: '',
      awardDate: null,
      awardClassification: '',
      expectUpdateDate: null,
      withdrawDate: null,
      officialMark: null,
      outOfGpa: null,
      convertedMark: '',
      letterToGPARule: '',
      digitToLetterRule: '',
      marks: [],
      officialClassRanking: null,
      outOfRank: null,
      academicOnlineVerificationCode: '',
      degreeOnlineVerificationCode: '',
      certificates: [],
      transcripts: [],
      markExplanations: [],
      classRankProofs: [],
      otherDocuments: [],
    },
    enableEdit,
    entryYear,
    countryList: nationalityOptions,
    universityList,
    handleDelete,
    refetchApplication,
    handleLoading,
  } = props;
  const { applicationId = '' } = useParams();
  const { formModificationMode } = enableEdit;
  const isConditionEdit =
    formModificationMode === FormModificationMode.CONDITION_MEET;

  const {
    certificates,
    transcripts,
    markExplanations,
    classRankProofs,
    otherDocuments,
    academicQualificationId: initialAcademicQualificationId = '',
    ...others
  } = initialValues;
  const { marks = [] } = initialValues;

  // marks.length === 0 means the transcript provides a Award/Cum. GPA or Avg Mark
  const [ifCalculateCGAP, setIfCalculateCGAP] = useState<boolean>(
    (marks ?? [])?.length !== 0,
  );

  const [openDialogV1, setOpenDialogV1] = useState(false);
  const [openDialogV2, setOpenDialogV2] = useState(false);
  const [openMeetConditionDialog, setOpenMeetConditionDialog] = useState(false);

  const [currentStudyYearOptions, setCurrentStudyYearOptions] = useState<
    SelectOption[]
  >(initialCurrentStudyYearOptions);

  let alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'error',
  };

  const [academicQualificationId, setAcademicQualificationId] = useState(
    initialAcademicQualificationId,
  );
  const isCreateMode = !academicQualificationId;

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);

  const getCountryRegionLabel = (key: string | null): string => {
    return (
      nationalityOptions
        .find(option => option.value === key)
        ?.label?.toString() ?? ''
    );
  };

  const initialQualificationStatusOptions: SelectOption[] =
    entryYear != null
      ? initialQSFormat.map(option => {
          if (
            option.value ===
              QUALIFICATION_STATUS_TYPES.WILL_COMPLETE_STUDY_BEFORE ||
            option.value === QUALIFICATION_STATUS_TYPES.WILL_GRADUATE_IN_AFTER
          ) {
            return {
              ...option,
              label: `${option.label} ${convertEntryYearMap[entryYear]}`,
            };
          }
          return option;
        })
      : [];

  const [qualiStatusOptions, setQualiStatusOptions] = useState<SelectOption[]>(
    initialQualificationStatusOptions,
  );

  const [files, setFiles] = useState<SupportingDocuments>({
    certificates,
    transcripts,
    markExplanations,
    classRankProofs,
    otherDocuments,
  });

  const filesPanelInfo: Record<keyof SupportingDocuments, React.ReactNode> = {
    certificates: FILES_PANEL.CERTIFICATE,
    transcripts: FILES_PANEL.TRANSCRIPT,
    markExplanations: (
      <div>
        {FILES_PANEL.SYSTEM_EXPLANATION}
        <IconButton
          title={FILES_PANEL.SYSTEM_EXPLAIN_TOOLTIPS}
          sx={{
            marginBottom: '0.5em',
          }}
          icon={
            <PriorityHigh
              sx={{
                color: '#FFFFFF',
                backgroundColor: '#951D1D',
                '&:hover': { backgroundColor: '#951D1D' },
                borderRadius: '50%',
                width: '0.5em',
                height: '0.5em',
              }}
            />
          }
        />
      </div>
    ),
    classRankProofs: FILES_PANEL.RANKING_PROOF,
    otherDocuments: FILES_PANEL.OTHER_DOCUMENTS,
  };

  const handleFilesChange =
    (key: keyof SupportingDocuments) =>
    (order: number, id: string): boolean => {
      const fileArray = files[key];
      let updatedFileArray: FileData[] = fileArray;
      switch (order) {
        case FILE_MANAGE_ORDER.UPLOAD:
          updatedFileArray = [
            ...fileArray,
            {
              id,
              name: '',
              link: '',
            },
          ];
          break;
        case FILE_MANAGE_ORDER.REMOVE: {
          updatedFileArray = fileArray.filter(fileId => fileId.id !== id);
          break;
        }
        default:
          break;
      }
      const newfiles: NewFiles = {
        ...files,
        [key]: updatedFileArray,
      };
      setFiles(newfiles);

      return true;
    };

  const createAcademicQualification = addApplicationItem(
    'academicQualifications',
    refetchApplication,
  );
  const updateAcademicQualification = updateApplicationItem();
  const handleSubmit = async (
    values: AcademicQualificationFormData,
  ): Promise<void> => {
    await handleLoading(async () => {
      try {
        const {
          to,
          expectUpdateDate,
          awardDate,
          withdrawDate,
          awardClassification,
          convertedMark,
          outOfGpa,
          outOfRank,
          officialClassRanking,
          letterToGPARule,
          digitToLetterRule,
          institutionCountry,
          institutionName,
          rankIndicator = false,
          currentStudyYear,
          certificates,
          transcripts,
          markExplanations,
          classRankProofs,
          otherDocuments,
          gpaIndicator = false,
          presentIndicator = false,
          degreeCertIndicator,
          academicCredentialIndicator,
          officialMark,
          convertGPARuleId,
          ...others
        } = values;

        const validCurrentStudyYear = Boolean(currentStudyYear); // can not be '' | undefined | null | 0
        const input = {
          to: to === '' ? null : to,
          outOfGpa: outOfGpa === '' ? null : outOfGpa,
          rankIndicator,
          gpaIndicator,
          presentIndicator,
          degreeCertIndicator: Boolean(degreeCertIndicator),
          academicCredentialIndicator: Boolean(academicCredentialIndicator),
          outOfRank: rankIndicator ? null : outOfRank,
          officialClassRanking: rankIndicator ? null : officialClassRanking,
          expectUpdateDate: expectUpdateDate === '' ? null : expectUpdateDate,
          awardDate: awardDate === '' ? null : awardDate,
          withdrawDate: withdrawDate === '' ? null : withdrawDate,
          currentStudyYear: validCurrentStudyYear ? currentStudyYear : -1,
          awardClassification:
            awardClassification === '' ? null : awardClassification,
          institution: {
            country: institutionCountry,
            name: institutionName,
          },
          officialMark: String(officialMark),
          convertGPARuleId: convertGPARuleId ?? null,
          markConversion: {
            convertedMark: String(convertedMark),
            digitToLetterRule: { name: digitToLetterRule },
          },
          certificates: certificates.map((certificate, index) => {
            const { name, ...other } = certificate;
            return {
              name: handleFileRename(applicationNo, 'certificate', index + 1),
              ...other,
            };
          }),
          transcripts: transcripts.map((transcript, index) => {
            const { name, ...other } = transcript;
            return {
              name: handleFileRename(applicationNo, 'transcript', index + 1),
              ...other,
            };
          }),
          markExplanations: markExplanations.map((markExplanation, index) => {
            const { name, ...other } = markExplanation;
            return {
              name: handleFileRename(
                applicationNo,
                'markExplanation',
                index + 1,
              ),
              ...other,
            };
          }),
          classRankProofs: classRankProofs.map((classRankProof, index) => {
            const { name, ...other } = classRankProof;
            return {
              name: handleFileRename(
                applicationNo,
                'classRankProof',
                index + 1,
              ),
              ...other,
            };
          }),
          otherDocuments: otherDocuments.map((otherDocument, index) => {
            const { name, ...other } = otherDocument;
            return {
              name: handleFileRename(applicationNo, 'otherDocument', index + 1),
              ...other,
            };
          }),
          ...others,
        };
        if (isCreateMode) {
          const res = await createAcademicQualification({
            variables: {
              id: applicationId,
              input: {
                academicQualification: {
                  ...input,
                },
              },
            },
          });
          const resId =
            res.data?.addApplicationItem.academicQualifications[
              academicQualifyIndex
            ]._id;
          setAcademicQualificationId(resId);
        } else {
          const variables = {
            id: applicationId,
            updateId: academicQualificationId,
            input: {
              academicQualification: {
                ...input,
              },
            },
          };
          await updateAcademicQualification({
            variables,
          });
        }
        refetchApplication();
        alertDetailDef = {
          alertContent: isConditionEdit
            ? PAGE_TEXT.CONDITION_SAVE_ALTER_TEXT
            : 'Saved Academic Qualification Successfully.',
          alertType: 'success',
        };
      } catch (e) {
        alertDetailDef = {
          alertContent: `Saved Academic Qualification Failed.${
            (e as Error)?.message
          }`,
          alertType: 'error',
        };
      } finally {
        setAlertDetails(alertDetailDef);
      }

      setOpenAlert(true);
    });
  };

  const formik = useFormik<AcademicQualificationForm>({
    initialValues: {
      ...others,
    },
    validationSchema: object({
      presentIndicator: boolean().nullable(),
      degreeLevel: string().required('Required'),
      programmeName: string().required('Required'),
      from: string().required('Required'),
      to: string().when('presentIndicator', ([presentIndicator], schema) => {
        const present: boolean = presentIndicator;
        return present ? schema.notRequired() : schema.required('Required');
      }),
      studyMode: string().required('Required'),
      currentStudyYear: string().when(
        'presentIndicator',
        ([presentIndicator], schema) => {
          const present: boolean = presentIndicator;
          return present ? schema.required('Required') : schema.notRequired();
        },
      ),
      institutionName: string().required('Required'),
      institutionCountry: string().required('Required'),
      instructionMedium: string().required('Required'),
      otherInstructionMedium: string().when(
        'instructionMedium',
        ([instructionMedium], schema) => {
          const required: boolean = instructionMedium === 'OTHERS';
          return required ? schema.required('Required') : schema.notRequired();
        },
      ),
      studyField: string().required('Required'),
      qualificationMeans: string().required('Required'),
      qualificationStatus: string().required('Required'),
      awardDate: string().when(
        'qualificationStatus',
        ([qualificationStatus], schema) => {
          const required: boolean =
            qualificationStatus === QUALIFICATION_STATUS_TYPES.GRADUATED;
          return required ? schema.required('Required') : schema.notRequired();
        },
      ),
      expectUpdateDate: string().when(
        'qualificationStatus',
        ([qualificationStatus], schema) => {
          const required: boolean =
            qualificationStatus ===
              QUALIFICATION_STATUS_TYPES.WILL_COMPLETE_STUDY_BEFORE ||
            qualificationStatus ===
              QUALIFICATION_STATUS_TYPES.WILL_GRADUATE_IN_AFTER;
          return required ? schema.required('Required') : schema.notRequired();
        },
      ),
      withdrawDate: string().when(
        'qualificationStatus',
        ([qualificationStatus], schema) => {
          const required: boolean =
            qualificationStatus === QUALIFICATION_STATUS_TYPES.WITHDRAW;
          return required ? schema.required('Required') : schema.notRequired();
        },
      ),
      awardClassification: string().when(
        'qualificationStatus',
        ([qualificationStatus], schema) => {
          const required: boolean =
            qualificationStatus === QUALIFICATION_STATUS_TYPES.GRADUATED;
          return required ? schema.required('Required') : schema.notRequired();
        },
      ),
      officialMark: number().when(
        'gpaIndicator',
        ([gpaIndicator]: boolean[], schema) => {
          return gpaIndicator
            ? schema.notRequired()
            : schema.required('Required').test({
                message: 'Invalid value',
                test: value => value >= 0,
              });
        },
      ),
      outOfGpa: number().when(
        ['gpaIndicator', 'officialMark'],
        ([gpaIndicator, officialMark], schema) => {
          return (gpaIndicator as boolean)
            ? schema.notRequired()
            : schema.required('Required').test({
                message: 'Invalid value',
                test: value =>
                  (officialMark as boolean)
                    ? value >= officialMark && value >= 0
                    : value >= 0,
              });
        },
      ),
      convertedMark: number().when(
        'gpaIndicator',
        ([gpaIndicator]: boolean[], schema) => {
          return gpaIndicator
            ? schema.notRequired()
            : schema.required('Required').test({
                message: 'Invalid value',
                test: value => value >= 0,
              });
        },
      ),
      officialClassRanking: number().when(
        'rankIndicator',
        ([rankIndicator], schema) => {
          return (rankIndicator as boolean)
            ? schema.notRequired()
            : schema.required('Required').test({
                message: 'Invalid value',
                test: value => value >= 0,
              });
        },
      ),
      outOfRank: number().when(
        ['rankIndicator', 'officialClassRanking'],
        ([rankIndicator, officialClassRanking], schema) => {
          return (rankIndicator as boolean)
            ? schema.notRequired()
            : schema.required('Required').test({
                message: 'Invalid value',
                test: value => value >= officialClassRanking && value >= 0,
              });
        },
      ),
      academicOnlineVerificationCode: string().when(
        'academicCredentialIndicator',
        ([academicCredentialIndicator]: boolean[], schema) => {
          return academicCredentialIndicator
            ? schema.required('Required')
            : schema.notRequired();
        },
      ),
      degreeOnlineVerificationCode: string().when(
        'degreeCertIndicator',
        ([degreeCertIndicator]: boolean[], schema) => {
          return degreeCertIndicator
            ? schema.required('Required')
            : schema.notRequired();
        },
      ),
    }),
    onSubmit: async (values: AcademicQualificationForm): Promise<void> => {
      const conditions = [
        {
          // No need upload certificate condition:
          // 1. Present Study
          // 2. qualification status is COMPLETED_WITHOUT_AWARD (RPG-1839)
          condition:
            formik.values.qualificationStatus !==
              QUALIFICATION_STATUS_TYPES.COMPLETED_WITHOUT_AWARD &&
            !formik.values.presentIndicator &&
            files.certificates.length === 0, // have graduated but no certificate
          hint: PAGE_TEXT.QUALIFICATION.ACADEMIC_QUALIFICATION.FILES_PANEL
            .UPLOAD_CERTIFICATE_HINT,
        },
        {
          condition:
            !formik.values.gpaIndicator && files.transcripts.length === 0, // have gpa but no proof
          hint: PAGE_TEXT.QUALIFICATION.ACADEMIC_QUALIFICATION.FILES_PANEL
            .UPLOAD_TRANSCRIPT_HINT,
        },
        {
          // Mark Explanatory Note should be required when applicant indicate has GPA. The condition is the same as transcript file.
          condition:
            !formik.values.gpaIndicator && files.markExplanations.length === 0,
          hint: PAGE_TEXT.QUALIFICATION.ACADEMIC_QUALIFICATION.FILES_PANEL
            .UPLOAD_MARK_EXPLAIN,
        },
        {
          condition:
            !formik.values.rankIndicator && files.classRankProofs.length === 0, // have class ranking but no proof
          hint: PAGE_TEXT.QUALIFICATION.ACADEMIC_QUALIFICATION.FILES_PANEL
            .UPLOAD_CLASS_RANK_HINT,
        },
      ];

      for (const { condition, hint } of conditions) {
        if (condition) {
          setAlertDetails({
            alertType: 'warning',
            alertContent: hint,
          });
          setOpenAlert(true);
          return;
        }
      }

      const academicQualificationFormData: AcademicQualificationFormData = {
        ...files,
        ...values,
      };
      await handleSubmit(academicQualificationFormData);
    },
  });

  const academicQualifyPermission = editAcademicQualifyPermission(
    initialValues,
    {
      certificates,
      transcripts,
      markExplanations,
      classRankProofs,
      otherDocuments,
      ...formik.values,
    },
    enableEdit,
  );

  const prevValues = useRef({
    institutionName: formik.values.institutionName,
    institutionCountry: formik.values.institutionCountry,
  });

  const filterCurrentStudyYearOptions = (): void => {
    if (formik.values.from !== '' && formik.values.from !== null) {
      const fromTime = formik.values.from;

      const yearDifference = dayjs().diff(dayjs(fromTime), 'years');

      if (yearDifference >= 0 && yearDifference < 10) {
        const newFilteredOptions = Array.from(
          { length: yearDifference + 1 },
          (_, i) => i,
        ).map((_, i) => ({
          value: i + 1,
          label: (i + 1).toString(),
        }));
        setCurrentStudyYearOptions(newFilteredOptions);
      } else {
        setCurrentStudyYearOptions(initialCurrentStudyYearOptions);
      }
    }
  };

  useEffect(() => {
    filterCurrentStudyYearOptions();
  }, [formik.values.from, formik.values.to, formik.values.degreeCertIndicator]);

  const handleOpenDialog = (): void => {
    setOpenDialogV2(true);
  };

  const handleDialogV2Apply = async (
    dialogContent: DialogV2ContentValue,
  ): Promise<void> => {
    await formik.setValues({
      ...formik.values,
      convertedMark: dialogContent?.convertedGPA.toString(),
      convertGPARuleId: dialogContent?.ruleId ?? '',
    });
  };

  const handleCalculateCGAPIndicator = async (
    SyntheticBaseEvent: any,
  ): Promise<void> => {
    const needCal = SyntheticBaseEvent.target.value === PAGE_TEXT.NO;
    setIfCalculateCGAP(needCal);
    if (needCal) await formik.setFieldValue('officialMark', '');
    else await formik.setFieldValue('marks', []);
  };

  const handleDialogV2Close = async (
    dialogContent: DialogV2ContentValue,
  ): Promise<void> => {
    setOpenDialogV2(false);
    await formik.setValues({
      ...formik.values,
      // to keep the history
      convertGPARuleId: dialogContent.ruleId,
    });
  };

  async function handlePresentIndicator(): Promise<void> {
    await formik.setValues({
      ...formik.values,
      presentIndicator: !formik.values.presentIndicator,
      to: '',
      currentStudyYear: '',
      qualificationStatus: '',
      awardDate: null,
      expectUpdateDate: null,
      withdrawDate: null,
      awardClassification: '',
    });
    if (!formik.values.presentIndicator) {
      const filteredQualiStatusOptions =
        initialQualificationStatusOptions.filter(option => {
          return option.value !== 'GRADUATED';
        });
      setQualiStatusOptions(filteredQualiStatusOptions);
    } else {
      setQualiStatusOptions(initialQualificationStatusOptions);
    }
  }

  async function handleNoMark(): Promise<void> {
    if (!formik.values.gpaIndicator) {
      await formik.setFieldValue('officialMark', '');
      await formik.setFieldValue('convertedMark', '');
      await formik.setFieldValue('outOfGpa', '');
      await formik.setFieldValue('marks', []);
      await formik.setFieldValue('digitToLetterRule', '');
      setIfCalculateCGAP(false);
    }

    await formik.setFieldValue('gpaIndicator', !formik.values.gpaIndicator);
  }

  async function handleNoRank(): Promise<void> {
    if (!formik.values.rankIndicator) {
      await formik.setFieldValue('officialClassRanking', '');
      await formik.setFieldValue('outOfRank', '');
    }
    await formik.setFieldValue('rankIndicator', !formik.values.rankIndicator);
  }

  async function changeQualificationStatus(
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    await formik.setValues({
      ...formik.values,
      qualificationStatus: e.target.value,
      awardDate: null,
      expectUpdateDate: null,
      withdrawDate: null,
      awardClassification:
        formik.values.institutionCountry === CHN
          ? AWARD_CLASSIFICATION_TYPE.OTHERS
          : '',
    });
  }

  const handleDialogV1Close = async (
    dialogContent: DialogContentValue,
  ): Promise<void> => {
    setOpenDialogV1(false);
    await formik.setValues({
      ...formik.values,
      // to keep the history
      marks: dialogContent.courses,
      digitToLetterRule: dialogContent.digitToLetterRule,
    });
  };

  const handleDialogV1Apply = async (
    dialogContent: DialogContentValue,
  ): Promise<void> => {
    await formik.setValues({
      ...formik.values,
      // to apply the officialMark and set keep the history
      officialMark: dialogContent.officialMark,
      marks: dialogContent.courses,
      digitToLetterRule: dialogContent.digitToLetterRule,

      // clear convertedMark to require user to convert again
      convertedMark: '',
    });
  };

  const handleChangeInstructionMedium = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const value = e.target.value;
    await formik.setFieldValue('instructionMedium', value);
    await formik.setFieldValue('otherInstructionMedium', '');
  };

  useEffect(() => {
    const { institutionName, institutionCountry } = formik.values;

    if (
      institutionName !== prevValues.current.institutionName ||
      institutionCountry !== prevValues.current.institutionCountry
    ) {
      // when institutionName or institutionCountry changes, clear convertedMark to require user to convert again
      void formik.setFieldValue('convertedMark', '');
      void formik.setFieldValue('digitToLetterRule', '');

      prevValues.current = { institutionName, institutionCountry };
    }
  }, [formik.values.institutionName, formik.values.institutionCountry]);

  const handleDisplayInstitutionName = useMemo((): any => {
    return (
      <SearchableSelect
        name="institutionName"
        options={universityList}
        required
        disabled={!academicQualifyPermission.institutionName}
        label={NAME_OF_INSTITUTION}
        inputLabelProps={{ shrink: true }}
        value={formik.values.institutionName}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={
          formik.touched.institutionName &&
          Boolean(formik.errors.institutionName)
        }
        helperText={
          formik.touched.institutionName && formik.errors.institutionName
        }
      />
    );
  }, [
    formik.values.institutionName,
    formik.touched.institutionName,
    formik.errors.institutionName,
    formik.isSubmitting,
    academicQualifyPermission.institutionName,
  ]);

  const showConvertGPA =
    Boolean(formik.values.officialMark) &&
    !(formik.touched.officialMark && Boolean(formik.errors.officialMark)) &&
    Boolean(formik.values.outOfGpa) &&
    !(formik.touched.outOfGpa && Boolean(formik.errors.outOfGpa));

  const handleChangeDegreeLevel = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const degreeLevel = e.target.value;
    if (degreeLevel === DEGREE_LEVEL.BACHELOR_DEGREE) {
      await formik.setFieldValue(
        'qualificationMeans',
        QUALIFICATION_MEANS.COURSEWORK,
      );
    } else await formik.setFieldValue('qualificationMeans', '');

    await formik.setFieldValue('degreeLevel', degreeLevel);
  };

  const handleChangeCountry = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const country = e.target.value;
    if (country === CHN) {
      await formik.setFieldValue(
        'awardClassification',
        AWARD_CLASSIFICATION_TYPE.OTHERS,
      );
    } else await formik.setFieldValue('awardClassification', '');

    await formik.setFieldValue('institutionCountry', country);
  };

  const handleNavToMeetCondition = (): void => {
    window.location.href =
      '/application/' +
      applicationId +
      '?apsb=' +
      APPLICATION_MODULE.APPLICATION_PROGRESS +
      '&aphb=' +
      APPLICATION_STATUS_MODULE.CONDITIONAL_OFFER_CONFIRMATION;
  };

  const handleCloseMeetConditionDialog = (): void => {
    setOpenMeetConditionDialog(false);
  };
  const handleOpenMeetConditionDialog = (): void => {
    setOpenMeetConditionDialog(true);
  };

  const graduatedStatus =
    formik.values.qualificationStatus === QUALIFICATION_STATUS_TYPES.GRADUATED;
  const withdrawStatus =
    formik.values.qualificationStatus === QUALIFICATION_STATUS_TYPES.WITHDRAW;
  const willUpdateResult =
    formik.values.qualificationStatus ===
      QUALIFICATION_STATUS_TYPES.WILL_COMPLETE_STUDY_BEFORE ||
    formik.values.qualificationStatus ===
      QUALIFICATION_STATUS_TYPES.WILL_GRADUATE_IN_AFTER;

  return (
    <Container>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Dialog
        openDialog={openMeetConditionDialog}
        dialogTitle={''}
        dialogAction={PAGE_TEXT.GO_SUBMIT}
        cancelAction={PAGE_TEXT.BACK_TO_CHECK}
        maxWidth={'sm'}
        onDialogAction={handleNavToMeetCondition}
        onDialogClose={handleCloseMeetConditionDialog}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {PAGE_TEXT.QUALIFICATION.DIALOG_NOTE}
        </Typography>
      </Dialog>
      <ContentBox>
        <form
          onSubmit={handleReadySubmit(formik, academicQualifyIndex)}
          // prevent browser validate, otherwise will block formik setTouched when submitting
          noValidate
        >
          <Box sx={{ flexGrow: 1 }}>
            <div className="row-field">
              <Grid container columnSpacing={4} rowSpacing={2}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Select
                    name="degreeLevel"
                    className="w-full"
                    required
                    label={LEVEL_OF_DEGREE}
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.degreeLevel}
                    onChange={handleChangeDegreeLevel}
                    onBlur={formik.handleBlur}
                    options={levelOfDegreeOptions}
                    disabled={!academicQualifyPermission.degreeLevel}
                    error={
                      formik.touched.degreeLevel &&
                      Boolean(formik.errors.degreeLevel)
                    }
                    helperText={
                      formik.touched.degreeLevel && formik.errors.degreeLevel
                    }
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <TextField
                    name="programmeName"
                    className="w-full"
                    required
                    label={NAME_OF_PROGRAMME_AWARD}
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.programmeName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!academicQualifyPermission.programmeName}
                    error={
                      formik.touched.programmeName &&
                      Boolean(formik.errors.programmeName)
                    }
                    helperText={
                      formik.touched.programmeName &&
                      formik.errors.programmeName
                    }
                  />
                </Grid>
                <Grid
                  size={{
                    md: 12,
                  }}
                >
                  <span>
                    <FormControlLabel
                      control={
                        <SwitchButton
                          checked={formik.values.presentIndicator}
                          onChange={handlePresentIndicator}
                          disabled={!academicQualifyPermission.presentIndicator}
                        />
                      }
                      label={PRESENT}
                    />
                  </span>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <DatePicker
                    name="from"
                    className="w-full"
                    required
                    label={FROM}
                    disabled={!academicQualifyPermission.from}
                    type="date"
                    maxDate={dayjs()}
                    InputLabelProps={{ shrink: true }}
                    value={
                      formik.values.from !== null
                        ? dayjs(formik.values.from)
                        : null
                    }
                    onChange={(date: any) => {
                      void formik.setFieldValue('from', date);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.from && Boolean(formik.errors.from)}
                    helperText={formik.touched.from && formik.errors.from}
                  />
                </Grid>
                {formik.values.presentIndicator ? (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <Select
                      name="currentStudyYear"
                      className="w-full"
                      required
                      disabled={!academicQualifyPermission.currentStudyYear}
                      label={CURRENT_STUDY_YEAR}
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.currentStudyYear}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      options={currentStudyYearOptions}
                      error={
                        formik.touched.currentStudyYear &&
                        Boolean(formik.errors.currentStudyYear)
                      }
                      helperText={
                        formik.touched.currentStudyYear &&
                        formik.errors.currentStudyYear
                      }
                    />
                  </Grid>
                ) : (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <DatePicker
                      name="to"
                      className="w-full"
                      required
                      disabled={!academicQualifyPermission.to}
                      label={TO}
                      minDate={dayjs(formik.values.from)}
                      maxDate={dayjs()}
                      InputLabelProps={{ shrink: true }}
                      value={
                        formik.values.to !== null
                          ? dayjs(formik.values.to)
                          : null
                      }
                      onChange={(date: any) => {
                        void formik.setFieldValue('to', date);
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.to && Boolean(formik.errors.to)}
                      helperText={formik.touched.to && formik.errors.to}
                    />
                  </Grid>
                )}
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Select
                    name="studyMode"
                    className="w-full"
                    required
                    disabled={!academicQualifyPermission.studyMode}
                    label={MODE_OF_STUDY}
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.studyMode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    options={modeOfStudyOptions}
                    error={
                      formik.touched.studyMode &&
                      Boolean(formik.errors.studyMode)
                    }
                    helperText={
                      formik.touched.studyMode && formik.errors.studyMode
                    }
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  {handleDisplayInstitutionName}
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Select
                    name="institutionCountry"
                    className="w-full"
                    required
                    disabled={!academicQualifyPermission.institutionCountry}
                    label={COUNTRY_REGION_OF_INSTITUTION}
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.institutionCountry}
                    onChange={handleChangeCountry}
                    onBlur={formik.handleBlur}
                    options={nationalityOptions}
                    error={
                      formik.touched.institutionCountry &&
                      Boolean(formik.errors.institutionCountry)
                    }
                    helperText={
                      formik.touched.institutionCountry &&
                      formik.errors.institutionCountry
                    }
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Select
                    name="instructionMedium"
                    className="w-full"
                    required
                    disabled={!academicQualifyPermission.instructionMedium}
                    label={MEDIUM_OF_INSTRUCTION}
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.instructionMedium}
                    onChange={handleChangeInstructionMedium}
                    onBlur={formik.handleBlur}
                    options={mediumOfInstructionOptions}
                    error={
                      formik.touched.instructionMedium &&
                      Boolean(formik.errors.instructionMedium)
                    }
                    helperText={
                      formik.touched.instructionMedium &&
                      formik.errors.instructionMedium
                    }
                  />
                </Grid>
                {formik.values.instructionMedium === 'OTHERS' && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <TextField
                      name="otherInstructionMedium"
                      className="w-full"
                      required
                      disabled={
                        !academicQualifyPermission.otherInstructionMedium
                      }
                      label={OTHER_MEDIUM}
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.otherInstructionMedium}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.otherInstructionMedium &&
                        Boolean(formik.errors.otherInstructionMedium)
                      }
                      helperText={
                        formik.touched.otherInstructionMedium &&
                        formik.errors.otherInstructionMedium
                      }
                    />
                  </Grid>
                )}

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Select
                    name="studyField"
                    className="w-full"
                    required
                    disabled={!academicQualifyPermission.studyField}
                    label={FIELD_OF_STUDY}
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.studyField}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    options={fieldOfStudyOptions}
                    error={
                      formik.touched.studyField &&
                      Boolean(formik.errors.studyField)
                    }
                    helperText={
                      formik.touched.studyField && formik.errors.studyField
                    }
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Select
                    name="qualificationMeans"
                    className="w-full"
                    required
                    disabled={!academicQualifyPermission.qualificationMeans}
                    label={MEDIUM_OF_QUALIFICATION}
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.qualificationMeans}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    options={mediumOfQualificationOptions}
                    error={
                      formik.touched.qualificationMeans &&
                      Boolean(formik.errors.qualificationMeans)
                    }
                    helperText={
                      formik.touched.qualificationMeans &&
                      formik.errors.qualificationMeans
                    }
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Select
                    name="qualificationStatus"
                    className="w-full"
                    required
                    disabled={!academicQualifyPermission.qualificationStatus}
                    label={QUALIFICATION_STATUS}
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.qualificationStatus}
                    onChange={changeQualificationStatus}
                    onBlur={formik.handleBlur}
                    options={qualiStatusOptions}
                    error={
                      formik.touched.qualificationStatus &&
                      Boolean(formik.errors.qualificationStatus)
                    }
                    helperText={
                      formik.touched.qualificationStatus &&
                      formik.errors.qualificationStatus
                    }
                  />
                </Grid>
                {graduatedStatus && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <DatePicker
                      name="awardDate"
                      className="w-full"
                      required
                      disabled={!academicQualifyPermission.awardDate}
                      label={DATE_OF_AWARD}
                      InputLabelProps={{ shrink: true }}
                      value={
                        formik.values.awardDate !== null
                          ? dayjs(formik.values.awardDate)
                          : null
                      }
                      onBlur={formik.handleBlur}
                      onChange={(date: any) => {
                        void formik.setFieldValue('awardDate', date);
                      }}
                      error={
                        formik.touched.awardDate &&
                        Boolean(formik.errors.awardDate)
                      }
                      helperText={
                        formik.touched.awardDate && formik.errors.awardDate
                      }
                    />
                  </Grid>
                )}
                {willUpdateResult && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <DatePicker
                      name="expectUpdateDate"
                      className="w-full"
                      required
                      disabled={!academicQualifyPermission.expectUpdateDate}
                      label={EXPECTED_DATE}
                      minDate={dayjs()}
                      InputLabelProps={{ shrink: true }}
                      value={
                        formik.values.expectUpdateDate !== null
                          ? dayjs(formik.values.expectUpdateDate)
                          : null
                      }
                      onBlur={formik.handleBlur}
                      onChange={(date: any) => {
                        void formik.setFieldValue('expectUpdateDate', date);
                      }}
                      error={
                        formik.touched.expectUpdateDate &&
                        Boolean(formik.errors.expectUpdateDate)
                      }
                      helperText={
                        formik.touched.expectUpdateDate &&
                        formik.errors.expectUpdateDate
                      }
                    />
                  </Grid>
                )}
                {withdrawStatus && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <DatePicker
                      name="withdrawDate"
                      className="w-full"
                      required
                      disabled={!academicQualifyPermission.withdrawDate}
                      label={WITHDRAWAL_DATE}
                      InputLabelProps={{ shrink: true }}
                      value={
                        formik.values.withdrawDate !== null
                          ? dayjs(formik.values.withdrawDate)
                          : null
                      }
                      onBlur={formik.handleBlur}
                      onChange={(date: any) => {
                        void formik.setFieldValue('withdrawDate', date);
                      }}
                      error={
                        formik.touched.withdrawDate &&
                        Boolean(formik.errors.withdrawDate)
                      }
                      helperText={
                        formik.touched.withdrawDate &&
                        formik.errors.withdrawDate
                      }
                    />
                  </Grid>
                )}
                {graduatedStatus && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <Select
                      name="awardClassification"
                      className="w-full"
                      required
                      disabled={!academicQualifyPermission.awardClassification}
                      label={AWARD_CLASSIFICATION}
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.awardClassification}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      options={awardClassificationOptions}
                      error={
                        formik.touched.awardClassification &&
                        Boolean(formik.errors.awardClassification)
                      }
                      helperText={
                        formik.touched.awardClassification &&
                        formik.errors.awardClassification
                      }
                    />
                  </Grid>
                )}
                {formik.values.institutionCountry === 'CHN' && (
                  <>
                    <Grid
                      size={{
                        md: 12,
                      }}
                    >
                      <Checkbox
                        name="academicCredentialIndicator"
                        label={
                          <div className="mt-2">
                            {HAVE_ACADEMIC_CREDENTIAL}
                            <div
                              className="py-2"
                              dangerouslySetInnerHTML={{
                                __html: ACADEMIC_CREDENTIAL_INTRO,
                              }}
                            />
                          </div>
                        }
                        disabled={
                          !academicQualifyPermission.academicCredentialIndicator
                        }
                        onChange={formik.handleChange}
                        value={formik.values.academicCredentialIndicator}
                        sx={{
                          alignItems: 'flex-start',
                        }}
                      />

                      {formik.values.academicCredentialIndicator && (
                        <Grid className="pl-6 mt-4">
                          <TextField
                            name={'academicOnlineVerificationCode'}
                            className="w-full"
                            label={ONLINE_VERIFICATION}
                            type="text"
                            disabled={
                              !academicQualifyPermission.academicOnlineVerificationCode
                            }
                            InputLabelProps={{ shrink: true }}
                            value={formik.values.academicOnlineVerificationCode}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            required
                            error={
                              formik.touched.academicOnlineVerificationCode &&
                              Boolean(
                                formik.errors.academicOnlineVerificationCode,
                              )
                            }
                            helperText={
                              formik.touched.academicOnlineVerificationCode &&
                              formik.errors.academicOnlineVerificationCode
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                    <Grid
                      size={{
                        md: 12,
                      }}
                    >
                      <Checkbox
                        name="degreeCertIndicator"
                        label={
                          <div className="mt-2">
                            {HAVE_DEGREE_CERTIFICATE}
                            <div
                              className="py-2"
                              dangerouslySetInnerHTML={{
                                __html: DEGREE_CERTIFICATE_INTRO,
                              }}
                            />
                          </div>
                        }
                        disabled={
                          !academicQualifyPermission.degreeCertIndicator
                        }
                        onChange={formik.handleChange}
                        value={formik.values.degreeCertIndicator}
                        sx={{
                          alignItems: 'flex-start',
                        }}
                      />
                      {formik.values.degreeCertIndicator && (
                        <Grid className="pl-6 mt-4">
                          <TextField
                            name={'degreeOnlineVerificationCode'}
                            className="w-full"
                            required
                            disabled={
                              !academicQualifyPermission.degreeOnlineVerificationCode
                            }
                            label={DEGREE_VERIFICATION}
                            type="text"
                            InputLabelProps={{ shrink: true }}
                            value={formik.values.degreeOnlineVerificationCode}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.degreeOnlineVerificationCode &&
                              Boolean(
                                formik.errors.degreeOnlineVerificationCode,
                              )
                            }
                            helperText={
                              formik.touched.degreeOnlineVerificationCode &&
                              formik.errors.degreeOnlineVerificationCode
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
              <div className="ml-4">
                {Object.entries({
                  certificates: files.certificates,
                  otherDocuments: files.otherDocuments,
                }).map(([key, item], index) => {
                  const fileType = key as keyof SupportingDocuments;
                  return (
                    <FilesPanel
                      key={key}
                      disable={!academicQualifyPermission[fileType]}
                      disableDeleteOnly={!enableDelete}
                      title={
                        index === 0
                          ? `${FILES_PANEL.PANEL_TITLE} 
                          `
                          : undefined
                      }
                      caption={filesPanelInfo[fileType]}
                      files={item}
                      handleFilesChange={handleFilesChange(fileType)}
                      handleLoading={handleLoading}
                      popoverContent={
                        fileType === 'certificates'
                          ? FILES_PANEL.UPLOAD_CERTIFICATE_ALERT
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            </div>
            <div className="field-title">{OFFICIAL_MARK}</div>
            <div className="flex flex-row items-start">
              <div className="w-full">
                <div className="px-4 mt-4">
                  <Checkbox
                    name="gpaIndicator"
                    disabled={!academicQualifyPermission.gpaIndicator}
                    label={NO_OFFICIAL_MARK_GPA}
                    onChange={handleNoMark}
                    value={formik.values.gpaIndicator}
                  />
                </div>
                <div className="mx-12 mb-2">
                  <h2
                    className={`mt-2 ${!academicQualifyPermission.officialMark ? 'text-[#BDBDBD]' : ''}`}
                  >
                    {HAVE_CGPA}
                  </h2>
                  <div className="w-full flex mt-2 items-center">
                    <Radio
                      value={PAGE_TEXT.YES}
                      disabled={!academicQualifyPermission.officialMark}
                      label={PAGE_TEXT.YES}
                      onChange={handleCalculateCGAPIndicator}
                      checked={!ifCalculateCGAP}
                    />
                    <Radio
                      value={PAGE_TEXT.NO}
                      disabled={!academicQualifyPermission.officialMark}
                      label={
                        <>
                          {PAGE_TEXT.NO}{' '}
                          {ifCalculateCGAP && (
                            <span
                              className={`text-sm ${
                                !academicQualifyPermission.officialMark
                                  ? ''
                                  : 'text-[#951d1d]'
                              }`}
                            >
                              {'('}
                              {HELPER_TEXT.NO_CGPA}
                              <IconButton
                                icon={
                                  <Calculate
                                    style={{
                                      color:
                                        academicQualifyPermission.officialMark
                                          ? '#951d1d'
                                          : 'inherit',
                                    }}
                                  />
                                }
                                disabled={
                                  !academicQualifyPermission.officialMark
                                }
                                onClick={() => {
                                  setOpenDialogV1(true);
                                }}
                              />
                              {')'}
                            </span>
                          )}
                        </>
                      }
                      onChange={handleCalculateCGAPIndicator}
                      checked={ifCalculateCGAP}
                    />
                  </div>
                </div>

                <div className="row-field">
                  <div style={{ width: '100%' }}>
                    <Grid
                      container
                      columnSpacing={4}
                      rowSpacing={2}
                      className="pl-12"
                    >
                      <Grid
                        size={{
                          xs: 12,
                          md: 6,
                        }}
                      >
                        <TextField
                          className="w-full"
                          label={AWARD_CUM_GPA_AVG_MARK}
                          placeholder={
                            formik.values.gpaIndicator
                              ? ''
                              : ifCalculateCGAP
                                ? 'Filled by GPA/Mark Calculator'
                                : 'eg: 3.5'
                          }
                          required={!formik.values.gpaIndicator}
                          type="number"
                          name="officialMark"
                          disabled={!academicQualifyPermission.officialMark}
                          value={formik.values.officialMark}
                          InputLabelProps={{ shrink: true }}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          inputProps={{
                            min: 0,
                          }}
                          error={
                            formik.touched.officialMark &&
                            Boolean(formik.errors.officialMark)
                          }
                          helperText={
                            formik.touched.officialMark &&
                            formik.errors.officialMark
                          }
                          InputProps={{
                            readOnly: ifCalculateCGAP,
                          }}
                        />
                      </Grid>

                      {openDialogV1 && (
                        <GPAConversionDialog
                          viewMode={false}
                          dialogTitle={GPA_DIALOG.DIALOG_V1_TITLE}
                          openDialog={openDialogV1}
                          onDialogClose={handleDialogV1Close}
                          onDialogApply={handleDialogV1Apply}
                          dialogContent={{
                            courses: formik.values.marks,
                            officialMark: formik.values.officialMark ?? '',
                            digitToLetterRule: formik.values.digitToLetterRule,
                          }}
                        />
                      )}

                      <Grid
                        size={{
                          xs: 12,
                          md: 6,
                        }}
                      >
                        <TextField
                          className="w-full"
                          label={
                            <span>
                              {OUT_OF}

                              <IconButton
                                title={HELPER_TEXT.OUT_OF}
                                icon={
                                  <Help
                                    sx={{
                                      fontSize: 18,
                                      color: '#951D1D',
                                    }}
                                  />
                                }
                              />
                            </span>
                          }
                          placeholder={
                            formik.values.gpaIndicator ? '' : 'eg: 4.0'
                          }
                          type="number"
                          required={!formik.values.gpaIndicator}
                          name="outOfGpa"
                          disabled={!academicQualifyPermission.outOfGpa}
                          value={formik.values.outOfGpa}
                          InputLabelProps={{ shrink: true }}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          inputProps={{
                            min: formik.values.officialMark,
                          }}
                          error={
                            formik.touched.outOfGpa &&
                            Boolean(formik.errors.outOfGpa)
                          }
                          helperText={
                            formik.touched.outOfGpa && formik.errors.outOfGpa
                          }
                        />
                      </Grid>
                      {showConvertGPA && (
                        <>
                          <Grid
                            size={{
                              md: 12,
                            }}
                          >
                            <h2
                              className={
                                !academicQualifyPermission.officialMark
                                  ? 'text-[#BDBDBD]'
                                  : ''
                              }
                            >
                              {HELPER_TEXT.CONVERTED_SCORE}
                            </h2>
                          </Grid>

                          <Grid
                            size={{
                              xs: 12,
                              md: 6,
                            }}
                          >
                            <TextField
                              className="w-full"
                              label={CONVERTED_SCORE}
                              fullWidth
                              required={!formik.values.gpaIndicator}
                              name="convertedMark"
                              placeholder="Filled by GPA Converter"
                              disabled={
                                !academicQualifyPermission.convertedMark
                              }
                              InputLabelProps={{ shrink: true }}
                              value={formik.values.convertedMark}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.convertedMark &&
                                Boolean(formik.errors.convertedMark)
                              }
                              helperText={
                                formik.touched.convertedMark &&
                                formik.errors.convertedMark
                              }
                              InputProps={{
                                readOnly: true,
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      icon={
                                        <Calculate
                                          style={{
                                            color:
                                              academicQualifyPermission.convertedMark
                                                ? '#951d1d'
                                                : 'inherit',
                                          }}
                                        />
                                      }
                                      disabled={
                                        !academicQualifyPermission.convertedMark
                                      }
                                      onClick={handleOpenDialog}
                                      title="GPA Convert Tool(Base 4.0)"
                                    />
                                  </InputAdornment>
                                ),
                              }}
                            />

                            {openDialogV2 && (
                              <GPAConversionDialogV2
                                viewMode={false}
                                dialogTitle={GPA_DIALOG.DIALOG_V2_TITLE}
                                openDialog={openDialogV2}
                                onDialogClose={handleDialogV2Close}
                                onDialogApply={handleDialogV2Apply}
                                institutionName={formik.values.institutionName}
                                officialMark={Number(
                                  formik.values.officialMark ?? 0,
                                )}
                                outOfGpa={Number(formik.values.outOfGpa ?? 100)}
                                convertGPARuleId={
                                  formik.values.convertGPARuleId ?? ''
                                }
                                convertedMark={Number(
                                  formik.values.convertedMark ?? 0,
                                )}
                                countryRegion={getCountryRegionLabel(
                                  formik.values.institutionCountry,
                                )}
                              />
                            )}
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </div>
                </div>
              </div>
              <div className="mt-4 ml-4">
                {Object.entries({
                  transcripts: files.transcripts,
                  markExplanations: files.markExplanations,
                }).map(([key, item], index) => {
                  const fileType = key as keyof SupportingDocuments;
                  return (
                    <FilesPanel
                      key={key}
                      disable={!academicQualifyPermission[fileType]}
                      disableDeleteOnly={!enableDelete}
                      title={
                        index === 0 ? `${FILES_PANEL.PANEL_TITLE}` : undefined
                      }
                      caption={filesPanelInfo[fileType]}
                      files={item}
                      handleFilesChange={handleFilesChange(fileType)}
                      handleLoading={handleLoading}
                      popoverContent={
                        fileType === 'transcripts'
                          ? FILES_PANEL.UPLOAD_TRANSCRIPT_ALERT
                          : FILES_PANEL.UPLOAD_EXPLANATORY_NOTE_ALERT
                      }
                    />
                  );
                })}
              </div>
            </div>

            <div className="field-title !mt-12">{RANK_IN_CLASS}</div>

            <div className="flex flex-row items-start">
              <div className="flex-1 mt-2">
                <div className="px-4 mt-4 w-full">
                  <Checkbox
                    name="rankIndicator"
                    label={NO_OFFICIAL_RANK}
                    disabled={!academicQualifyPermission.rankIndicator}
                    value={formik.values.rankIndicator}
                    onChange={handleNoRank}
                  />
                </div>
                <Grid
                  container
                  columnSpacing={4}
                  rowSpacing={2}
                  className="pl-12"
                >
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <TextField
                      name="officialClassRanking"
                      className="w-full"
                      placeholder={formik.values.rankIndicator ? '' : 'eg: 1'}
                      required
                      disabled={!academicQualifyPermission.officialClassRanking}
                      label={RANK}
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.officialClassRanking}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      type="number"
                      inputProps={{
                        min: 0,
                      }}
                      error={
                        formik.touched.officialClassRanking &&
                        Boolean(formik.errors.officialClassRanking)
                      }
                      helperText={
                        formik.touched.officialClassRanking &&
                        formik.errors.officialClassRanking
                      }
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <TextField
                      name="outOfRank"
                      className="w-full"
                      placeholder={formik.values.rankIndicator ? '' : 'eg: 180'}
                      required
                      disabled={!academicQualifyPermission.outOfRank}
                      label={OUT_OF}
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.outOfRank}
                      type="number"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      inputProps={{
                        min: formik.values.officialClassRanking,
                      }}
                      error={
                        formik.touched.outOfRank &&
                        Boolean(formik.errors.outOfRank)
                      }
                      helperText={
                        formik.touched.outOfRank && formik.errors.outOfRank
                      }
                    />
                  </Grid>
                </Grid>
              </div>
              <div className="mt-4 ml-4">
                <FilesPanel
                  disable={!academicQualifyPermission.classRankProofs}
                  disableDeleteOnly={!enableDelete}
                  title={`${FILES_PANEL.PANEL_TITLE}`}
                  caption={FILES_PANEL.RANKING_PROOF}
                  files={files.classRankProofs}
                  handleFilesChange={handleFilesChange('classRankProofs')}
                  popoverContent={FILES_PANEL.UPLOAD_RANKING_PROOF_ALERT}
                  handleLoading={handleLoading}
                />
              </div>
            </div>
          </Box>

          <div className="buttons-area">
            {academicQualifyPermission.enableSave && (
              <Button variant="contained" type="submit">
                {PAGE_TEXT.SAVE}
              </Button>
            )}
            {academicQualifyPermission.enableSaveCondition && (
              <Button
                variant="contained"
                onClick={handleOpenMeetConditionDialog}
                sx={{ marginLeft: '2rem' }}
              >
                {PAGE_TEXT.QUALIFICATION.CONDITION_MEET}
              </Button>
            )}
            {academicQualifyPermission.enableDelete &&
              enableDelete &&
              academicQualifyIndex > 0 && (
                <Button
                  onClick={() => {
                    handleDelete(academicQualifyIndex);
                  }}
                  variant="outlined"
                >
                  {PAGE_TEXT.DELETE}
                </Button>
              )}
          </div>
        </form>
      </ContentBox>
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  width: 100%;

  .field-title {
    width: 100%;
    font-size: 1rem;
    font-weight: 600;
    margin: 1rem 1rem 0 1rem;
  }

  .row-field {
    display: flex;
    flex-direction: row;
    width: 100%;
  }

  .MuiSwitch-root {
    margin: 1rem;
  }

  form {
    margin-bottom: 2rem;
    width: 100%;
    padding-top: 0.5rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .input-area {
    display: flex;
    flex-direction: column;
  }

  .buttons-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    margin-top: 1rem;
    button {
      margin: 0 1rem;
    }
  }

  .MuiDialog-paper {
    width: 50% !important;

    .MuiFormControl-root {
      width: 100% !important;
    }
  }
`;

const ContentBox = styled.div`
  margin-top: 35px;
  flex: 1;
  display: flex;
  flex-direction: row;

  .MuiGrid-container {
    margin-top: 0;
    margin-left: 0;
    width: calc(100%);
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .input-area {
    display: flex;
    flex-direction: column;
  }
`;
